import DevStorage from './devStorage';
import PedigreeService from './pedigreeService';
import WebApi from './webApi';
import { IS_WEB } from './platform';
import { BIRD_FIELDS, toDbValue } from '../shared/birdFields';
import {
  formatDateKey,
  isDiaryEntryEmpty,
  normalizeDiaryEntry,
  parseDateKey,
  startOfWeek
} from '../utils/diaryWeek';
import { DIARY_FIELD_KEYS, normalizeDiaryPatch } from '../shared/diaryFields';
import { findSharedPigeonConflicts } from '../utils/shareDuplicates';

// Main-process enforcement errors arrive as "LICENSE_BLOCKED:<code>:<message>"
// (wrapped in Electron's IPC error prefix). Surface them as clean, flagged errors.
const translateLicenseError = (error) => {
  const match = String(error?.message || '').match(/LICENSE_BLOCKED:([\w-]+):([\s\S]*)$/);
  if (!match) return error;
  const clean = new Error(match[2].trim());
  clean.code = match[1];
  clean.isLicenseError = true;
  return clean;
};

// Database service for React components
class DatabaseService {
  // Non-Electron data source: the web API's server-backed mirror on web
  // builds, localStorage in browser development. Same shape either way, so
  // the read paths below serve both platforms.
  static async getData() {
    if (IS_WEB) return WebApi.getData();
    return DevStorage.getDevStorage();
  }

  // Core query/run methods — raw SQL exists only on desktop, where the
  // Electron main process executes it. Web and dev builds use the
  // platform-specific method branches below, never raw SQL.
  static async query(sql, params = []) {
    if (window.electronAPI) {
      return await window.electronAPI.dbQuery(sql, params);
    }
    throw new Error('Raw SQL queries are only available in the desktop app.');
  }

  static async run(sql, params = []) {
    if (window.electronAPI) {
      try {
        return await window.electronAPI.dbRun(sql, params);
      } catch (error) {
        throw translateLicenseError(error);
      }
    }
    throw new Error('Raw SQL statements are only available in the desktop app.');
  }

  // Multiple statements as ONE transaction (desktop only) — for delete and
  // rename cascades that must land together or not at all. Takes
  // [[sql, params], ...]; falls back to nothing off desktop (web/dev have
  // their own batch-shaped branches).
  static async runBatch(statements) {
    if (window.electronAPI) {
      try {
        return await window.electronAPI.dbRunBatch(
          statements.map(([query, params = []]) => ({ query, params }))
        );
      } catch (error) {
        throw translateLicenseError(error);
      }
    }
    throw new Error('Raw SQL statements are only available in the desktop app.');
  }

  // Real (non-test) birds on this install — used for the trial cap UI so it
  // matches the cap enforced in the main process and every other visible count.
  static async getBirdCount() {
    if (window.electronAPI) {
      const rows = await this.query('SELECT COUNT(*) as count FROM birds WHERE (is_test IS NULL OR is_test = 0)');
      return rows[0]?.count || 0;
    }
    return (await this.getData()).birds.filter(b => !b.is_test).length;
  }

  // Bird operations
  static async getAllBirds(includeTest = false) {
    if (window.electronAPI) {
      return await this.query(`
        SELECT b.*,
               s.name as sire_name,
               d.name as dam_name
        FROM birds b
        LEFT JOIN birds s ON b.sire_id = s.id
        LEFT JOIN birds d ON b.dam_id = d.id
        ${includeTest ? '' : 'WHERE (b.is_test IS NULL OR b.is_test = 0)'}
        ORDER BY b.created_at DESC
      `);
    }
    const data = await this.getData();
    const byId = new Map(data.birds.map(b => [b.id, b]));
    return data.birds
      .filter(bird => includeTest || !bird.is_test)
      .map(bird => ({
        ...bird,
        sire_name: byId.get(bird.sire_id)?.name || null,
        dam_name: byId.get(bird.dam_id)?.name || null
      })).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  static async getRecentBirds(limit = 5) {
    if (window.electronAPI) {
      return await this.query(`
        SELECT * FROM birds
        WHERE (is_test IS NULL OR is_test = 0)
        ORDER BY created_at DESC
        LIMIT ?
      `, [limit]);
    }
    const data = await this.getData();
    return data.birds
      .filter(b => !b.is_test)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, limit);
  }

  static async getBirdById(id) {
    if (window.electronAPI) {
      const birds = await this.query(`
        SELECT b.*,
               s.name as sire_name, s.ring_number as sire_ring_number,
               d.name as dam_name, d.ring_number as dam_ring_number
        FROM birds b
        LEFT JOIN birds s ON b.sire_id = s.id
        LEFT JOIN birds d ON b.dam_id = d.id
        WHERE b.id = ?
      `, [id]);
      return birds.length > 0 ? birds[0] : null;
    }
    const data = await this.getData();
    const bird = data.birds.find(b => b.id == id);
    if (!bird) return null;
    const sire = data.birds.find(b => b.id === bird.sire_id);
    const dam = data.birds.find(b => b.id === bird.dam_id);
    return {
      ...bird,
      sire_name: sire?.name || null,
      sire_ring_number: sire?.ring_number || null,
      dam_name: dam?.name || null,
      dam_ring_number: dam?.ring_number || null
    };
  }

  static async getBirdsForAutocomplete(searchTerm = '', includeTest = false) {
    const testFilter = includeTest ? '' : 'AND (is_test IS NULL OR is_test = 0)';
    if (window.electronAPI) {
      const query = searchTerm
        ? `SELECT id, name, ring_number, sex FROM birds WHERE (name LIKE ? OR ring_number LIKE ?) ${testFilter} ORDER BY name`
        : `SELECT id, name, ring_number, sex FROM birds WHERE 1=1 ${testFilter} ORDER BY name`;
      const params = searchTerm ? [`%${searchTerm}%`, `%${searchTerm}%`] : [];
      return await this.query(query, params);
    }
    const data = await this.getData();
    let birds = data.birds
      .filter(bird => includeTest || !bird.is_test)
      .map(bird => ({
        id: bird.id, name: bird.name, ring_number: bird.ring_number, sex: bird.sex
      }));
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      birds = birds.filter(bird =>
        (bird.name && bird.name.toLowerCase().includes(term)) ||
        (bird.ring_number && bird.ring_number.toLowerCase().includes(term))
      );
    }
    return birds.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  static async createBird(birdData) {
    const { name, ring_number, sex, color, breed, year, notes, body_photo, eye_photo, sire_id, dam_id, is_test } = birdData;

    if (window.electronAPI) {
      const result = await this.run(`
        INSERT INTO birds (name, ring_number, sex, color, breed, year, notes, body_photo, eye_photo, sire_id, dam_id, is_test)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `, [
        name || null, ring_number || null, sex || null, color || null, breed || null,
        toDbValue('year', year), notes || null, body_photo || null, eye_photo || null,
        toDbValue('sire_id', sire_id), toDbValue('dam_id', dam_id), is_test ? 1 : 0
      ]);
      return result.lastID;
    }
    if (IS_WEB) {
      const bird = await WebApi.createBird({
        name, ring_number, sex, color, breed, year, notes,
        body_photo, eye_photo, sire_id, dam_id, is_test,
        // Present only on imports — preserves the original dates so a
        // desktop backup restores with its history intact (mirrors the
        // desktop restore path's COALESCE).
        created_at: birdData.created_at,
        updated_at: birdData.updated_at
      });
      return bird.id;
    }
    const data = DevStorage.getDevStorage();
    const newBird = {
      id: data.nextId++, name: name || '', ring_number: ring_number || null,
      sex: sex || null, color: color || null, breed: breed || null,
      year: toDbValue('year', year) || new Date().getFullYear(),
      notes: notes || null, body_photo: body_photo || null, eye_photo: eye_photo || null,
      sire_id: toDbValue('sire_id', sire_id), dam_id: toDbValue('dam_id', dam_id),
      is_test: is_test ? 1 : 0,
      created_at: new Date().toISOString(), updated_at: new Date().toISOString()
    };
    data.birds.push(newBird);
    DevStorage.setDevStorage(data);
    return newBird.id;
  }

  static async checkDuplicateRingNumber(ringLetters, ringNumbers, year, excludeId = null) {
    // Only check for duplicates if all 3 fields are provided
    if (!ringLetters || !ringNumbers || !year) return false;
    const combinedRing = `${ringLetters} ${ringNumbers}`;
    if (window.electronAPI) {
      const query = `
        SELECT id FROM birds WHERE UPPER(ring_number) = ? AND year = ? ${excludeId ? 'AND id != ?' : ''} LIMIT 1
      `;
      const params = [combinedRing.toUpperCase(), year];
      if (excludeId) params.push(excludeId);
      const result = await this.query(query, params);
      return result.length > 0;
    }
    const data = await this.getData();
    return data.birds.some(bird =>
      bird.ring_number && bird.ring_number.toUpperCase() === combinedRing.toUpperCase() &&
      bird.year === year && (excludeId ? bird.id != excludeId : true)
    );
  }

  static async getTestBirds() {
    if (window.electronAPI) {
      return await this.query(`
        SELECT b.*,
               s.name as sire_name, s.ring_number as sire_ring_number,
               d.name as dam_name, d.ring_number as dam_ring_number
        FROM birds b
        LEFT JOIN birds s ON b.sire_id = s.id
        LEFT JOIN birds d ON b.dam_id = d.id
        WHERE b.is_test = 1
        ORDER BY b.created_at DESC
      `);
    }
    const data = await this.getData();
    const byId = new Map(data.birds.map(b => [b.id, b]));
    return data.birds.filter(b => b.is_test).map(bird => {
      const sire = byId.get(bird.sire_id);
      const dam = byId.get(bird.dam_id);
      return {
        ...bird,
        sire_name: sire?.name || null,
        sire_ring_number: sire?.ring_number || null,
        dam_name: dam?.name || null,
        dam_ring_number: dam?.ring_number || null
      };
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  // Partial update: only columns present in birdData are written, so callers
  // that don't handle photos can never wipe them.
  static async updateBird(id, birdData) {
    if (window.electronAPI) {
      const sets = [];
      const params = [];
      for (const field of BIRD_FIELDS) {
        if (birdData[field] !== undefined) {
          sets.push(`${field} = ?`);
          params.push(toDbValue(field, birdData[field]));
        }
      }
      if (sets.length === 0) return;
      params.push(id);
      await this.run(`
        UPDATE birds SET ${sets.join(', ')}, updated_at = CURRENT_TIMESTAMP
        WHERE id = ?
      `, params);
    } else if (IS_WEB) {
      const partial = {};
      for (const field of BIRD_FIELDS) {
        if (birdData[field] !== undefined) partial[field] = birdData[field];
      }
      if (Object.keys(partial).length > 0) {
        await WebApi.updateBird(id, partial);
      }
    } else {
      const data = DevStorage.getDevStorage();
      const idx = data.birds.findIndex(b => b.id == id);
      if (idx !== -1) {
        const existing = data.birds[idx];
        const updated = { ...existing };
        for (const field of BIRD_FIELDS) {
          if (birdData[field] !== undefined) {
            updated[field] = toDbValue(field, birdData[field]);
          }
        }
        updated.updated_at = new Date().toISOString();
        data.birds[idx] = updated;
        DevStorage.setDevStorage(data);
      }
    }
  }

  static async deleteBird(id) {
    if (window.electronAPI) {
      // One transaction: parents unlinked, results and chart rows removed
      // BEFORE the bird row (foreign_keys = ON checks per statement), and
      // nothing is applied if any step fails.
      await this.runBatch([
        ['UPDATE birds SET sire_id = NULL WHERE sire_id = ?', [id]],
        ['UPDATE birds SET dam_id = NULL WHERE dam_id = ?', [id]],
        ['DELETE FROM race_results WHERE bird_id = ?', [id]],
        ['DELETE FROM chart_birds WHERE bird_id = ?', [id]],
        ['DELETE FROM birds WHERE id = ?', [id]]
      ]);
    } else if (IS_WEB) {
      await WebApi.deleteBird(id);
    } else {
      const data = DevStorage.getDevStorage();
      data.birds = data.birds
        .map(b => ({ ...b, sire_id: b.sire_id == id ? null : b.sire_id, dam_id: b.dam_id == id ? null : b.dam_id }))
        .filter(b => b.id != id);
      data.raceResults = (data.raceResults || []).filter(r => r.bird_id != id);
      data.chartBirds = (data.chartBirds || []).filter(cb => cb.bird_id != id);
      DevStorage.setDevStorage(data);
    }
  }

  // ---- race results ------------------------------------------------------
  // One result = one race recorded at up to three levels (Club/Fed/Combine
  // by default, labels editable per row), each with a position and a bird
  // count. Columns match the race_results table and the web-server normalizer.
  static RESULT_FIELDS = [
    'race', 'race_date',
    't1_label', 't1_pos', 't1_birds',
    't2_label', 't2_pos', 't2_birds',
    't3_label', 't3_pos', 't3_birds'
  ];

  // Shared user-text normalizer: trim, cap at 120 to match the web-server
  // normalizer (so a long race or chart name can't store in full on desktop
  // but truncate on web), empty becomes null.
  static normalizeText(v) {
    return (typeof v === 'string' && v.trim()) ? v.trim().slice(0, 120) : null;
  }

  static normalizeResult(data) {
    const textFields = new Set(['race', 'race_date', 't1_label', 't2_label', 't3_label']);
    const out = {};
    for (const f of this.RESULT_FIELDS) {
      const v = data[f];
      if (textFields.has(f)) {
        out[f] = this.normalizeText(v);
      } else {
        const n = Math.floor(Number(v));
        out[f] = (v === '' || v === null || v === undefined || !Number.isFinite(n) || n < 0) ? null : n;
      }
    }
    return out;
  }

  static async getRaceResults(birdId) {
    if (window.electronAPI) {
      return await this.query('SELECT * FROM race_results WHERE bird_id = ? ORDER BY id', [birdId]);
    }
    const data = await this.getData();
    return (data.raceResults || []).filter(r => r.bird_id == birdId);
  }

  static async addRaceResult(birdId, resultData) {
    const r = this.normalizeResult(resultData);
    // Optional link to a season-chart column: a result created from a chart
    // cell carries the chart_race_id so both views stay one dataset. Plain
    // profile results leave it unset.
    const link = Math.floor(Number(resultData.chart_race_id));
    const chartRaceId = Number.isFinite(link) && link > 0 ? link : null;
    if (window.electronAPI) {
      const fields = chartRaceId ? [...this.RESULT_FIELDS, 'chart_race_id'] : this.RESULT_FIELDS;
      const values = chartRaceId ? [...this.RESULT_FIELDS.map(f => r[f]), chartRaceId] : this.RESULT_FIELDS.map(f => r[f]);
      const res = await this.run(`
        INSERT INTO race_results (bird_id, ${fields.join(', ')})
        VALUES (?, ${fields.map(() => '?').join(', ')})
      `, [birdId, ...values]);
      return res.lastID;
    }
    if (IS_WEB) {
      const result = await WebApi.addRaceResult(birdId, chartRaceId ? { ...r, chart_race_id: chartRaceId } : r);
      return result.id;
    }
    const data = DevStorage.getDevStorage();
    const newResult = {
      id: data.nextId++, bird_id: Number(birdId), ...r, chart_race_id: chartRaceId,
      created_at: new Date().toISOString(), updated_at: new Date().toISOString()
    };
    data.raceResults = data.raceResults || [];
    data.raceResults.push(newResult);
    DevStorage.setDevStorage(data);
    return newResult.id;
  }

  static async updateRaceResult(id, resultData) {
    const r = this.normalizeResult(resultData);
    if (window.electronAPI) {
      const sets = this.RESULT_FIELDS.map(f => `${f} = ?`).join(', ');
      await this.run(
        `UPDATE race_results SET ${sets}, updated_at = CURRENT_TIMESTAMP WHERE id = ?`,
        [...this.RESULT_FIELDS.map(f => r[f]), id]
      );
    } else if (IS_WEB) {
      await WebApi.updateRaceResult(id, r);
    } else {
      const data = DevStorage.getDevStorage();
      const idx = (data.raceResults || []).findIndex(x => x.id == id);
      if (idx !== -1) {
        data.raceResults[idx] = { ...data.raceResults[idx], ...r, updated_at: new Date().toISOString() };
        DevStorage.setDevStorage(data);
      }
    }
  }

  static async deleteRaceResult(id) {
    if (window.electronAPI) {
      await this.run('DELETE FROM race_results WHERE id = ?', [id]);
    } else if (IS_WEB) {
      await WebApi.deleteRaceResult(id);
    } else {
      const data = DevStorage.getDevStorage();
      data.raceResults = (data.raceResults || []).filter(x => x.id != id);
      DevStorage.setDevStorage(data);
    }
  }

  // All results across every bird — used by the client-built backup on
  // desktop/dev (the web export is streamed server-side and already includes
  // them).
  static async getAllRaceResults() {
    if (window.electronAPI) {
      return await this.query('SELECT * FROM race_results ORDER BY id');
    }
    const data = await this.getData();
    return data.raceResults || [];
  }

  // ---- season charts -----------------------------------------------------
  // A chart tracks one organisation's season: columns are chart_races (the
  // race program), rows are chart_birds (the pigeons on the chart), and each
  // cell is an ordinary race_results row linked by chart_race_id. Deleting
  // chart structure only UNLINKS results — recorded placings always survive
  // on the bird's profile; the only way a result dies is deleting the result
  // itself (from a cell or the profile — same row either way).

  static async getCharts() {
    if (window.electronAPI) {
      return await this.query('SELECT * FROM race_charts ORDER BY id');
    }
    const data = await this.getData();
    return [...(data.charts || [])].sort((a, b) => a.id - b.id);
  }

  static async createChart({ name, season }) {
    const cleanName = this.normalizeText(name);
    const cleanSeason = this.normalizeText(season);
    if (window.electronAPI) {
      const res = await this.run('INSERT INTO race_charts (name, season) VALUES (?, ?)', [cleanName, cleanSeason]);
      return res.lastID;
    }
    if (IS_WEB) {
      const chart = await WebApi.createChart({ name: cleanName, season: cleanSeason });
      return chart.id;
    }
    const data = DevStorage.getDevStorage();
    const chart = {
      id: data.nextId++, name: cleanName, season: cleanSeason,
      created_at: new Date().toISOString(), updated_at: new Date().toISOString()
    };
    data.charts.push(chart);
    DevStorage.setDevStorage(data);
    return chart.id;
  }

  static async updateChart(id, { name, season }) {
    const cleanName = this.normalizeText(name);
    const cleanSeason = this.normalizeText(season);
    if (window.electronAPI) {
      await this.run(
        'UPDATE race_charts SET name = ?, season = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?',
        [cleanName, cleanSeason, id]
      );
    } else if (IS_WEB) {
      await WebApi.updateChart(id, { name: cleanName, season: cleanSeason });
    } else {
      const data = DevStorage.getDevStorage();
      const idx = data.charts.findIndex(c => c.id == id);
      if (idx !== -1) {
        data.charts[idx] = { ...data.charts[idx], name: cleanName, season: cleanSeason, updated_at: new Date().toISOString() };
        DevStorage.setDevStorage(data);
      }
    }
  }

  static async deleteChart(id) {
    if (window.electronAPI) {
      // One transaction, results unlinked first — mirrors the web worker's
      // atomic batch.
      await this.runBatch([
        [`UPDATE race_results SET chart_race_id = NULL
          WHERE chart_race_id IN (SELECT id FROM chart_races WHERE chart_id = ?)`, [id]],
        ['DELETE FROM chart_races WHERE chart_id = ?', [id]],
        ['DELETE FROM chart_birds WHERE chart_id = ?', [id]],
        ['DELETE FROM race_charts WHERE id = ?', [id]]
      ]);
    } else if (IS_WEB) {
      await WebApi.deleteChart(id);
    } else {
      const data = DevStorage.getDevStorage();
      const raceIds = new Set(data.chartRaces.filter(r => r.chart_id == id).map(r => r.id));
      data.raceResults = (data.raceResults || []).map(r =>
        raceIds.has(r.chart_race_id) ? { ...r, chart_race_id: null } : r);
      data.chartRaces = data.chartRaces.filter(r => r.chart_id != id);
      data.chartBirds = data.chartBirds.filter(b => b.chart_id != id);
      data.charts = data.charts.filter(c => c.id != id);
      DevStorage.setDevStorage(data);
    }
  }

  // Everything the chart grid needs in one call: the race columns (program
  // order), the pigeon rows (with display fields joined from birds), and the
  // linked results keyed for cell lookup.
  static async getChartDetail(chartId) {
    if (window.electronAPI) {
      const [races, birds, results] = await Promise.all([
        this.query('SELECT * FROM chart_races WHERE chart_id = ? ORDER BY sort, id', [chartId]),
        this.query(`
          SELECT cb.*, b.name AS bird_name, b.ring_number AS bird_ring, b.sex AS bird_sex
          FROM chart_birds cb LEFT JOIN birds b ON cb.bird_id = b.id
          WHERE cb.chart_id = ? ORDER BY cb.sort, cb.id
        `, [chartId]),
        this.query(`
          SELECT r.* FROM race_results r
          JOIN chart_races cr ON r.chart_race_id = cr.id
          WHERE cr.chart_id = ?
        `, [chartId])
      ]);
      return { races, birds, results };
    }
    const data = await this.getData();
    const byId = new Map(data.birds.map(b => [b.id, b]));
    const bySort = (a, b) => (a.sort || 0) - (b.sort || 0) || a.id - b.id;
    const races = (data.chartRaces || []).filter(r => r.chart_id == chartId).sort(bySort);
    const raceIds = new Set(races.map(r => r.id));
    const birds = (data.chartBirds || [])
      .filter(cb => cb.chart_id == chartId)
      .sort(bySort)
      .map(cb => {
        const bird = byId.get(cb.bird_id);
        return { ...cb, bird_name: bird?.name || null, bird_ring: bird?.ring_number || null, bird_sex: bird?.sex || null };
      });
    const results = (data.raceResults || []).filter(r => raceIds.has(r.chart_race_id));
    return { races, birds, results };
  }

  static async addChartRace(chartId, { race, race_date }) {
    const cleanRace = this.normalizeText(race);
    const cleanDate = this.normalizeText(race_date);
    if (window.electronAPI) {
      const res = await this.run(`
        INSERT INTO chart_races (chart_id, race, race_date, sort)
        VALUES (?, ?, ?, (SELECT COALESCE(MAX(sort), 0) + 1 FROM chart_races WHERE chart_id = ?))
      `, [chartId, cleanRace, cleanDate, chartId]);
      return res.lastID;
    }
    if (IS_WEB) {
      const row = await WebApi.addChartRace(chartId, { race: cleanRace, race_date: cleanDate });
      return row.id;
    }
    const data = DevStorage.getDevStorage();
    const sort = data.chartRaces.filter(r => r.chart_id == chartId)
      .reduce((max, r) => Math.max(max, r.sort || 0), 0) + 1;
    const row = {
      id: data.nextId++, chart_id: Number(chartId), race: cleanRace, race_date: cleanDate, sort,
      created_at: new Date().toISOString(), updated_at: new Date().toISOString()
    };
    data.chartRaces.push(row);
    DevStorage.setDevStorage(data);
    return row.id;
  }

  static async updateChartRace(id, { race, race_date }) {
    const cleanRace = this.normalizeText(race);
    const cleanDate = this.normalizeText(race_date);
    if (window.electronAPI) {
      // Linked results carry a copy of the race name/date for the bird
      // profile — one transaction keeps the copies in step with the column.
      await this.runBatch([
        ['UPDATE chart_races SET race = ?, race_date = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?',
          [cleanRace, cleanDate, id]],
        ['UPDATE race_results SET race = ?, race_date = ?, updated_at = CURRENT_TIMESTAMP WHERE chart_race_id = ?',
          [cleanRace, cleanDate, id]]
      ]);
    } else if (IS_WEB) {
      await WebApi.updateChartRace(id, { race: cleanRace, race_date: cleanDate });
    } else {
      const data = DevStorage.getDevStorage();
      const idx = data.chartRaces.findIndex(r => r.id == id);
      if (idx !== -1) {
        data.chartRaces[idx] = { ...data.chartRaces[idx], race: cleanRace, race_date: cleanDate, updated_at: new Date().toISOString() };
      }
      data.raceResults = (data.raceResults || []).map(r =>
        r.chart_race_id == id ? { ...r, race: cleanRace, race_date: cleanDate, updated_at: new Date().toISOString() } : r);
      DevStorage.setDevStorage(data);
    }
  }

  static async deleteChartRace(id) {
    if (window.electronAPI) {
      await this.runBatch([
        ['UPDATE race_results SET chart_race_id = NULL WHERE chart_race_id = ?', [id]],
        ['DELETE FROM chart_races WHERE id = ?', [id]]
      ]);
    } else if (IS_WEB) {
      await WebApi.deleteChartRace(id);
    } else {
      const data = DevStorage.getDevStorage();
      data.raceResults = (data.raceResults || []).map(r =>
        r.chart_race_id == id ? { ...r, chart_race_id: null } : r);
      data.chartRaces = data.chartRaces.filter(r => r.id != id);
      DevStorage.setDevStorage(data);
    }
  }

  static async addChartBird(chartId, birdId) {
    if (window.electronAPI) {
      // Adding the same pigeon twice is a no-op returning the existing row id.
      const dup = await this.query('SELECT id FROM chart_birds WHERE chart_id = ? AND bird_id = ?', [chartId, birdId]);
      if (dup.length > 0) return dup[0].id;
      const res = await this.run(`
        INSERT INTO chart_birds (chart_id, bird_id, sort)
        VALUES (?, ?, (SELECT COALESCE(MAX(sort), 0) + 1 FROM chart_birds WHERE chart_id = ?))
      `, [chartId, birdId, chartId]);
      return res.lastID;
    }
    if (IS_WEB) {
      const row = await WebApi.addChartBird(chartId, birdId);
      return row.id;
    }
    const data = DevStorage.getDevStorage();
    const dup = data.chartBirds.find(b => b.chart_id == chartId && b.bird_id == birdId);
    if (dup) return dup.id;
    const sort = data.chartBirds.filter(b => b.chart_id == chartId)
      .reduce((max, b) => Math.max(max, b.sort || 0), 0) + 1;
    const row = {
      id: data.nextId++, chart_id: Number(chartId), bird_id: Number(birdId), sort,
      created_at: new Date().toISOString()
    };
    data.chartBirds.push(row);
    DevStorage.setDevStorage(data);
    return row.id;
  }

  static async removeChartBird(id) {
    // Removes the ROW only — recorded results keep their chart_race_id
    // links, so re-adding the pigeon restores its cells.
    if (window.electronAPI) {
      await this.run('DELETE FROM chart_birds WHERE id = ?', [id]);
    } else if (IS_WEB) {
      await WebApi.removeChartBird(id);
    } else {
      const data = DevStorage.getDevStorage();
      data.chartBirds = data.chartBirds.filter(b => b.id != id);
      DevStorage.setDevStorage(data);
    }
  }

  // All chart structure across every chart — used by the client-built backup
  // on desktop/dev (the web export streams these server-side already).
  static async getAllChartData() {
    if (window.electronAPI) {
      const [charts, chartRaces, chartBirds] = await Promise.all([
        this.query('SELECT * FROM race_charts ORDER BY id'),
        this.query('SELECT * FROM chart_races ORDER BY sort, id'),
        this.query('SELECT * FROM chart_birds ORDER BY sort, id')
      ]);
      return { charts, chartRaces, chartBirds };
    }
    const data = await this.getData();
    return {
      charts: data.charts || [],
      chartRaces: data.chartRaces || [],
      chartBirds: data.chartBirds || []
    };
  }

  // ---- weekly diary -----------------------------------------------------
  // One row per calendar date. The page presents seven dates as columns;
  // storing dates independently keeps week navigation and backup merging
  // simple while the unique date key prevents duplicate daily logs.

  static async getDiaryEntries(start, end) {
    if (window.electronAPI) {
      return await this.query(
        'SELECT * FROM diary_entries WHERE entry_date >= ? AND entry_date <= ? ORDER BY entry_date',
        [start, end]
      );
    }
    if (IS_WEB) return WebApi.getDiaryEntries(start, end);
    const data = DevStorage.getDevStorage();
    return (data.diaryEntries || [])
      .filter((entry) => entry.entry_date >= start && entry.entry_date <= end)
      .sort((a, b) => a.entry_date.localeCompare(b.entry_date));
  }

  static async getAllDiaryEntries() {
    if (window.electronAPI) {
      return await this.query('SELECT * FROM diary_entries ORDER BY entry_date');
    }
    if (IS_WEB) return WebApi.getAllDiaryEntries();
    const data = DevStorage.getDevStorage();
    return [...(data.diaryEntries || [])].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
  }

  static async saveDiaryWeek(weekStart, entries) {
    const normalized = (Array.isArray(entries) ? entries : []).map(normalizeDiaryPatch);
    if (window.electronAPI) {
      const statements = normalized.flatMap((entry) => {
        const fields = DIARY_FIELD_KEYS.filter((field) => Object.prototype.hasOwnProperty.call(entry, field));
        if (fields.length === 0) return [];
        return [
          [
            `INSERT INTO diary_entries (entry_date, ${fields.join(', ')}, updated_at)
             VALUES (?, ${fields.map(() => '?').join(', ')}, CURRENT_TIMESTAMP)
             ON CONFLICT(entry_date) DO UPDATE SET
               ${fields.map((field) => `${field} = excluded.${field}`).join(', ')},
               updated_at = CURRENT_TIMESTAMP`,
            [entry.entry_date, ...fields.map((field) => entry[field] || null)]
          ],
          [
            `DELETE FROM diary_entries WHERE entry_date = ?
             AND COALESCE(feed, '') = '' AND COALESCE(water, '') = ''
             AND COALESCE(training, '') = '' AND COALESCE(notes, '') = ''`,
            [entry.entry_date]
          ]
        ];
      });
      if (statements.length > 0) await this.runBatch(statements);
      return;
    }
    if (IS_WEB) {
      await WebApi.saveDiaryWeek(weekStart, normalized);
      return;
    }

    const data = DevStorage.getDevStorage();
    const byDate = new Map((data.diaryEntries || []).map((entry) => [entry.entry_date, entry]));
    const now = new Date().toISOString();
    for (const patch of normalized) {
      const fields = DIARY_FIELD_KEYS.filter((field) => Object.prototype.hasOwnProperty.call(patch, field));
      if (fields.length === 0) continue;
      const existing = byDate.get(patch.entry_date);
      const merged = {
        ...existing,
        ...(existing ? {} : { id: data.nextId++, created_at: now }),
        ...patch,
        updated_at: now
      };
      if (isDiaryEntryEmpty(merged)) byDate.delete(patch.entry_date);
      else byDate.set(patch.entry_date, merged);
    }
    data.diaryEntries = [...byDate.values()];
    DevStorage.setDevStorage(data);
  }

  static async getOffspring(parentId) {
    if (window.electronAPI) {
      return await this.query('SELECT * FROM birds WHERE sire_id = ? OR dam_id = ? ORDER BY name', [parentId, parentId]);
    }
    const data = await this.getData();
    return data.birds
      .filter(b => b.sire_id == parentId || b.dam_id == parentId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  // Pedigree operations — delegated to PedigreeService
  static async getPedigree(birdId, generations = 4) {
    return PedigreeService.getPedigree((id) => this.getBirdById(id), birdId, generations);
  }

  static async findCommonAncestors(birdId, generations = 5) {
    return PedigreeService.findCommonAncestors((id) => this.getBirdById(id), birdId, generations);
  }

  // Settings operations
  static async getSetting(key) {
    if (window.electronAPI) {
      const result = await this.query('SELECT value FROM settings WHERE key = ?', [key]);
      return result.length > 0 ? result[0].value : null;
    }
    const data = await this.getData();
    return data.settings[key] || null;
  }

  static async getSettings() {
    if (window.electronAPI) {
      const result = await this.query('SELECT key, value FROM settings');
      const settings = {};
      result.forEach(row => { settings[row.key] = row.value; });
      return settings;
    }
    const data = await this.getData();
    return data.settings || {};
  }

  // Returns the value as STORED, which is not always the value passed in: the
  // web app hands large images off to object storage and keeps a URL instead.
  // Callers that display the setting afterwards should use the return value.
  static async setSetting(key, value) {
    if (window.electronAPI) {
      const strValue = (value === null || value === undefined) ? null : String(value);
      await this.run('INSERT OR REPLACE INTO settings (key, value, updated_at) VALUES (?, ?, CURRENT_TIMESTAMP)', [key, strValue]);
      return strValue;
    } else if (IS_WEB) {
      return await WebApi.setSetting(key, value);
    } else {
      const data = DevStorage.getDevStorage();
      data.settings[key] = value;
      DevStorage.setDevStorage(data);
      return value;
    }
  }

  // Statistics (real birds only — test pedigree birds are excluded)
  static async getStats() {
    if (window.electronAPI) {
      const notTest = '(is_test IS NULL OR is_test = 0)';
      const totalBirds = await this.query(`SELECT COUNT(*) as count FROM birds WHERE ${notTest}`);
      const maleCount = await this.query(`SELECT COUNT(*) as count FROM birds WHERE sex = ? AND ${notTest}`, ['male']);
      const femaleCount = await this.query(`SELECT COUNT(*) as count FROM birds WHERE sex = ? AND ${notTest}`, ['female']);
      const recentBirds = await this.query(`SELECT COUNT(*) as count FROM birds WHERE created_at > datetime('now', '-30 days') AND ${notTest}`);
      return {
        totalBirds: totalBirds[0]?.count || 0,
        maleCount: maleCount[0]?.count || 0,
        femaleCount: femaleCount[0]?.count || 0,
        recentBirds: recentBirds[0]?.count || 0
      };
    }
    const data = await this.getData();
    const real = data.birds.filter(b => !b.is_test);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return {
      totalBirds: real.length,
      maleCount: real.filter(b => b.sex === 'male').length,
      femaleCount: real.filter(b => b.sex === 'female').length,
      recentBirds: real.filter(b => new Date(b.created_at) > thirtyDaysAgo).length
    };
  }

  // Export/Import operations
  static async exportAllData() {
    try {
      if (IS_WEB && !window.electronAPI) {
        // Photos live in cloud storage as URLs; the server inlines them as
        // base64 so the file also restores into the desktop app.
        await WebApi.exportDownload();
        return { success: true, count: (await this.getData()).birds.length };
      }
      // Full backup: include test birds so a restore reproduces everything.
      const birds = await this.getAllBirds(true);
      const settings = await this.getSettings();
      const raceResults = await this.getAllRaceResults();
      const { charts, chartRaces, chartBirds } = await this.getAllChartData();
      const diaryEntries = await this.getAllDiaryEntries();
      const exportData = {
        version: '1.0.0', exportDate: new Date().toISOString(),
        birds, settings, raceResults, charts, chartRaces, chartBirds, diaryEntries,
        metadata: { totalBirds: birds.length, appVersion: '1.0.0' }
      };
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedigree-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return { success: true, count: birds.length };
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  // ---- bird sharing (web-only) -------------------------------------------

  // Send a bird (with its pedigree + photos) to another account by email.
  static async shareBird(birdId, email) {
    if (IS_WEB) return WebApi.shareBird(birdId, email);
    throw new Error('Sharing is only available in the web app.');
  }

  // The recipient's "Shared with me" inbox.
  static async getIncomingShares() {
    if (IS_WEB) return WebApi.listIncomingShares();
    return [];
  }

  // One shared package for preview + import: { share, package }.
  static async getShare(id) {
    if (IS_WEB) return WebApi.getShare(id);
    throw new Error('Sharing is only available in the web app.');
  }

  // Dismiss/decline a share, or clean up after a successful import.
  static async deleteShare(id) {
    if (IS_WEB) return WebApi.deleteShare(id);
    return { success: true };
  }

  // Accept a shared package into the loft. Reuses the proven import loop, but
  // resolves possible duplicates the recipient already owns instead of
  // duplicating them, and only imports the birds reachable from the shared
  // root (see importData's pruning). Overrides the trial-cap message so a
  // trial recipient reads share-appropriate copy, not backup/restore wording.
  static async acceptSharedBird(pkg, duplicateResolutions = {}) {
    return this.importData(pkg, {
      resolveSharedDuplicates: true,
      duplicateResolutions,
      overCapMessage: (n, max) =>
        `Adding this shared pedigree would put you over the free-trial limit of ${max} birds. ` +
        'Purchase a license to add it to your loft.'
    });
  }

  // Import strategy:
  // - Into an EMPTY database: restore birds with their original IDs so
  //   sire/dam relationships survive exactly as exported.
  // - Into a NON-EMPTY database: add all imported birds under fresh IDs and
  //   remap parent links between them. A shared-pigeon "replace" choice is the
  //   only path that deliberately modifies a selected existing record.
  static async importData(jsonData, {
    resolveSharedDuplicates = false,
    duplicateResolutions = {},
    overCapMessage = null
  } = {}) {
    try {
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      if (!data.birds || !Array.isArray(data.birds)) {
        throw new Error('Invalid data format: missing birds array');
      }

      // The trial pre-check and restore-mode decision must count what the
      // SERVER has, not a mirror loaded when the tab opened — a stale
      // undercount would start an import the server rejects part-way.
      if (IS_WEB && !window.electronAPI) WebApi.resetCache();
      const existingBirds = await this.getAllBirds(true);
      const restoreMode = existingBirds.length === 0;
      const strictImport = data.source === 'share';

      // Receive-side matches use ring NUMBERS + year and deliberately ignore
      // the leading ring letters. Every conflict requires an explicit choice;
      // never silently pick a local record or silently overwrite it.
      const resolutionByOldId = new Map();
      if (resolveSharedDuplicates) {
        const conflicts = findSharedPigeonConflicts(data.birds, existingBirds);
        const selectedExistingIds = new Set();
        for (const conflict of conflicts) {
          const choice = duplicateResolutions[conflict.incomingId];
          const existingId = Number(choice?.existingId);
          const candidateExists = conflict.candidates.some((bird) => bird.id === existingId);
          if (!candidateExists || !['keep', 'replace'].includes(choice?.action)) {
            throw new Error(
              'Possible duplicates changed or were not fully reviewed. Close this window, reopen the shared pigeon, and choose what to keep.'
            );
          }
          if (selectedExistingIds.has(existingId)) {
            throw new Error('Two incoming pigeons cannot use the same pigeon in your loft. Reopen the share and select different matches.');
          }
          selectedExistingIds.add(existingId);
          resolutionByOldId.set(conflict.incomingId, { existingId, action: choice.action });
        }
      }
      const resolutionFor = (bird) => resolveSharedDuplicates ? resolutionByOldId.get(bird.id) : null;
      const resolvedMatch = (bird) => resolutionFor(bird)?.existingId ?? null;
      // Old (package) ids we actually CREATED — so pass 2 relinks parents only
      // on new birds. Replacements are tracked separately and deliberately
      // rewrite the selected existing bird while preserving its database ID.
      const createdOldIds = new Set();
      const replacedOldIds = new Set();

      // When accepting a shared pedigree, import only the birds reachable from
      // the shared root WITHOUT climbing past an ancestor the recipient already
      // owns (matched by ring numbers + year). Birds above a kept match belong to the
      // recipient's existing bird's own ancestry — importing them would create
      // disconnected orphans and waste trial-cap slots. (Backups don't set
      // resolveSharedDuplicates or carry root_id, so their behaviour is unchanged.)
      let birdsToImport = data.birds;
      if (resolveSharedDuplicates && data.root_id != null) {
        const byId = new Map(data.birds.map((b) => [b.id, b]));
        const keep = new Set();
        const queue = [data.root_id];
        while (queue.length) {
          const bid = queue.shift();
          if (bid == null || keep.has(bid)) continue;
          const bird = byId.get(bid);
          if (!bird) continue;
          keep.add(bid);
          const resolution = resolutionFor(bird);
          if (resolution?.action === 'keep') continue; // preserve local ancestry
          if (bird.sire_id != null) queue.push(bird.sire_id);
          if (bird.dam_id != null) queue.push(bird.dam_id);
        }
        birdsToImport = data.birds.filter((b) => keep.has(b.id));
      }

      // A complete shared package must contain every parent needed by a newly
      // created or replaced bird. A "keep mine" conflict is the sole exception:
      // its incoming ancestry is intentionally not used.
      if (strictImport) {
        const importIds = new Set(birdsToImport.map((bird) => bird.id));
        for (const bird of birdsToImport) {
          if (resolutionFor(bird)?.action === 'keep') continue;
          for (const parentId of [bird.sire_id, bird.dam_id]) {
            if (parentId != null && !importIds.has(parentId)) {
              throw new Error('This shared pedigree is incomplete. Ask the sender to share the pigeon again.');
            }
          }
        }
      }

      let importedCount = 0;
      let updatedCount = 0;
      let skippedCount = 0;
      // Test/sandbox birds are a paid-only feature. When the app can't use
      // them, skip them on import (they're throwaway data) rather than letting
      // each one hit the main-process block and abort the whole restore.
      let skipTestBirds = false;

      // A trial install can't hold more than its bird allowance. Refuse an
      // over-cap import up front so we never leave a truncated, half-linked
      // restore behind and report it as success.
      let lic = null;
      if (window.electronAPI && window.electronAPI.getLicenseState) {
        lic = await window.electronAPI.getLicenseState();
      } else if (IS_WEB) {
        lic = await WebApi.licenseState();
      }
      if (lic) {
        skipTestBirds = !(lic.mode === 'full' || lic.mode === 'past_due');
        if (lic.mode === 'trial' && lic.trial) {
          const existingReal = existingBirds.filter(b => !b.is_test).length;
          // Reviewed possible duplicates are linked or replaced, not created,
          // so they don't consume trial-cap headroom — exclude them here or a
          // mostly-already-owned pedigree would be wrongly rejected.
          const incomingReal = birdsToImport.filter(b => !b.is_test && resolvedMatch(b) == null).length;
          if (existingReal + incomingReal > lic.trial.maxBirds) {
            const err = new Error(
              overCapMessage
                ? overCapMessage(incomingReal, lic.trial.maxBirds)
                : `This backup contains ${incomingReal} birds, but the free trial is limited to ${lic.trial.maxBirds}. ` +
                  `Purchase a license to restore your full backup.`
            );
            err.isLicenseError = true;
            throw err;
          }
        }
      }

      // Maps original (exported) ID -> ID in this database.
      const idMap = new Map();
      const createdLocalIds = [];
      let replacementStarted = false;

      // Pass 1: create every bird without parent links (parents may not
      // exist yet, and FK constraints are enforced).
      const importOne = async (bird) => {
        if (skipTestBirds && bird.is_test) { skippedCount++; return; }
        // A reviewed possible duplicate maps to the selected existing bird.
        const resolution = resolutionFor(bird);
        const matchedId = resolution?.existingId ?? null;
        if (matchedId != null) {
          if (bird.id != null) idMap.set(bird.id, matchedId);
          if (resolution.action === 'replace') replacedOldIds.add(bird.id);
          return;
        }
        try {
          if (restoreMode && bird.id != null && window.electronAPI) {
            await this.run(`
              INSERT INTO birds (id, name, ring_number, sex, color, breed, year, notes, body_photo, eye_photo, is_test, created_at, updated_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, COALESCE(?, CURRENT_TIMESTAMP), COALESCE(?, CURRENT_TIMESTAMP))
            `, [
              bird.id, bird.name || null, bird.ring_number || null, bird.sex || null,
              bird.color || null, bird.breed || null, toDbValue('year', bird.year),
              bird.notes || null, bird.body_photo || null, bird.eye_photo || null,
              bird.is_test ? 1 : 0, bird.created_at || null, bird.updated_at || null
            ]);
            idMap.set(bird.id, bird.id);
            createdOldIds.add(bird.id);
            createdLocalIds.push(bird.id);
          } else {
            const newId = await this.createBird({ ...bird, sire_id: null, dam_id: null });
            createdLocalIds.push(newId);
            if (bird.id != null) { idMap.set(bird.id, newId); createdOldIds.add(bird.id); }
          }
          importedCount++;
        } catch (error) {
          // A license block — or a dead session — must abort the whole
          // import, not grind through every remaining bird as a "skip".
          if (strictImport || error.isLicenseError || error.unauthenticated) throw error;
          console.error(`Error importing bird ${bird.id}:`, error);
          skippedCount++;
        }
      };

      // Pass 2: connect parents using the ID map.
      const linkOne = async (bird) => {
        if (bird.id == null || !idMap.has(bird.id)) return;
        const sireId = bird.sire_id != null ? (idMap.get(bird.sire_id) ?? null) : null;
        const damId = bird.dam_id != null ? (idMap.get(bird.dam_id) ?? null) : null;
        try {
          if (replacedOldIds.has(bird.id)) {
            // Replace the selected record in place. Keeping its local ID means
            // all existing offspring still refer to the same pigeon, while its
            // details, photos and ancestry become the incoming version.
            replacementStarted = true;
            await this.updateBird(idMap.get(bird.id), {
              name: bird.name ?? null,
              ring_number: bird.ring_number ?? null,
              sex: bird.sex ?? null,
              color: bird.color ?? null,
              breed: bird.breed ?? null,
              year: bird.year ?? null,
              notes: bird.notes ?? null,
              body_photo: bird.body_photo ?? null,
              eye_photo: bird.eye_photo ?? null,
              sire_id: sireId,
              dam_id: damId
            });
            updatedCount++;
            return;
          }
          if (!createdOldIds.has(bird.id) || (sireId == null && damId == null)) return;
          await this.updateBird(idMap.get(bird.id), { sire_id: sireId, dam_id: damId });
        } catch (error) {
          if (strictImport || error.isLicenseError || error.unauthenticated) throw error;
          console.error(`Error linking parents for bird ${bird.id}:`, error);
        }
      };

      try {
        if (IS_WEB && !window.electronAPI && !strictImport) {
        // Each bird is its own HTTP request on web — run a few in flight at
        // a time or big imports take minutes. Chunked Promise.all keeps the
        // abort-on-license-error semantics of the sequential path.
          const CHUNK = 4;
          for (let i = 0; i < birdsToImport.length; i += CHUNK) {
            await Promise.all(birdsToImport.slice(i, i + CHUNK).map(importOne));
          }
          for (let i = 0; i < birdsToImport.length; i += CHUNK) {
            await Promise.all(birdsToImport.slice(i, i + CHUNK).map(linkOne));
          }
        } else {
          // Shares run sequentially so a failure cannot leave a whole in-flight
          // chunk creating more partial records after the first error.
          for (const bird of birdsToImport) await importOne(bird);
          // Finish every new bird's ancestry before modifying existing records.
          for (const bird of birdsToImport.filter((item) => createdOldIds.has(item.id))) await linkOne(bird);
          for (const bird of birdsToImport.filter((item) => replacedOldIds.has(item.id))) await linkOne(bird);
        }
      } catch (error) {
        if (strictImport && createdLocalIds.length && !replacementStarted) {
          // Best-effort rollback of records created by this attempt. The share
          // stays in the inbox, so a network/session failure remains retryable.
          await Promise.allSettled([...createdLocalIds].reverse().map((birdId) => this.deleteBird(birdId)));
        }
        const wrapped = strictImport
          ? new Error(`The complete shared pedigree could not be added, so the share was kept for retry. ${error.message || ''}`.trim())
          : error;
        if (error.isLicenseError) wrapped.isLicenseError = true;
        if (error.unauthenticated) wrapped.unauthenticated = true;
        throw wrapped;
      }

      if (data.settings && typeof data.settings === 'object') {
        for (const [key, value] of Object.entries(data.settings)) {
          await this.setSetting(key, value);
        }
      }

      // Re-create the season charts before the race results so results can
      // re-link to their chart columns. Charts always import under fresh IDs
      // (they're cheap structure); the maps translate the backup's IDs.
      // Backups predating the feature simply have none of these arrays.
      // Failures here never fail the whole restore — the birds and pedigree
      // are already safely in.
      const chartIdMap = new Map();
      const chartRaceIdMap = new Map();
      if (Array.isArray(data.charts)) {
        for (const chart of data.charts) {
          try {
            // The web server requires a name and season (400 otherwise), and
            // the season selector hides charts with no season — default any
            // blanks so a hand-edited or legacy backup restores the same
            // grid on every platform instead of silently dropping it.
            const newId = await this.createChart({
              name: chart.name || 'Unnamed chart',
              season: chart.season || 'Unknown'
            });
            if (chart.id != null) chartIdMap.set(chart.id, newId);
          } catch (error) {
            if (error.isLicenseError || error.unauthenticated) throw error;
            console.error(`Error importing chart ${chart.id}:`, error);
          }
        }
      }
      if (Array.isArray(data.chartRaces)) {
        // Replay in program order — addChartRace assigns sort by insertion.
        const ordered = [...data.chartRaces].sort((a, b) => (a.sort || 0) - (b.sort || 0) || (a.id || 0) - (b.id || 0));
        for (const chartRace of ordered) {
          const newChartId = chartIdMap.get(chartRace.chart_id);
          if (newChartId == null) continue;
          try {
            const newId = await this.addChartRace(newChartId, chartRace);
            if (chartRace.id != null) chartRaceIdMap.set(chartRace.id, newId);
          } catch (error) {
            if (error.isLicenseError || error.unauthenticated) throw error;
            console.error(`Error importing chart race ${chartRace.id}:`, error);
          }
        }
      }
      if (Array.isArray(data.chartBirds)) {
        const ordered = [...data.chartBirds].sort((a, b) => (a.sort || 0) - (b.sort || 0) || (a.id || 0) - (b.id || 0));
        for (const chartBird of ordered) {
          const newChartId = chartIdMap.get(chartBird.chart_id);
          const newBirdId = chartBird.bird_id != null ? idMap.get(chartBird.bird_id) : null;
          if (newChartId == null || newBirdId == null) continue;
          try {
            await this.addChartBird(newChartId, newBirdId);
          } catch (error) {
            if (error.isLicenseError || error.unauthenticated) throw error;
            console.error(`Error importing chart bird ${chartBird.id}:`, error);
          }
        }
      }

      // Re-create race results against the birds that actually imported,
      // remapping each result's bird_id through the same idMap used for
      // parent links. Backups predating this feature simply have none. A
      // result whose bird was skipped (e.g. a test bird on a trial) is
      // dropped rather than orphaned; a result whose chart column didn't
      // import just loses the link and stays a plain profile result.
      if (Array.isArray(data.raceResults)) {
        for (const result of data.raceResults) {
          const newBirdId = result.bird_id != null ? idMap.get(result.bird_id) : null;
          if (newBirdId == null) continue;
          const newChartRaceId = result.chart_race_id != null ? (chartRaceIdMap.get(result.chart_race_id) ?? null) : null;
          try {
            await this.addRaceResult(newBirdId, { ...result, chart_race_id: newChartRaceId });
          } catch (error) {
            if (error.isLicenseError || error.unauthenticated) throw error;
            console.error(`Error importing race result for bird ${result.bird_id}:`, error);
          }
        }
      }

      // Diary dates are account-level rather than bird-linked. Restore dates
      // that are not already present, but never overwrite an existing logbook
      // entry during an import into a populated app.
      if (Array.isArray(data.diaryEntries)) {
        const existingDiary = await this.getAllDiaryEntries();
        const existingDates = new Set(existingDiary.map((entry) => entry.entry_date));
        const diaryByWeek = new Map();
        for (const rawEntry of data.diaryEntries) {
          const entry = normalizeDiaryEntry(rawEntry);
          const date = parseDateKey(entry.entry_date);
          if (!date || existingDates.has(entry.entry_date) || isDiaryEntryEmpty(entry)) continue;
          const week = formatDateKey(startOfWeek(date));
          if (!diaryByWeek.has(week)) diaryByWeek.set(week, []);
          diaryByWeek.get(week).push(entry);
        }
        for (const [week, weekEntries] of diaryByWeek) {
          try {
            await this.saveDiaryWeek(week, weekEntries);
          } catch (error) {
            if (error.isLicenseError || error.unauthenticated) throw error;
            console.error(`Error importing diary week ${week}:`, error);
          }
        }
      }
      return {
        success: true,
        imported: importedCount,
        updated: updatedCount,
        skipped: skippedCount,
        total: data.birds.length,
        restored: restoreMode
      };
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  }

  static async validateImportFile(file) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.birds || !Array.isArray(data.birds)) {
        return { valid: false, error: 'Invalid file format: missing birds data' };
      }
      return { valid: true, birdCount: data.birds.length, exportDate: data.exportDate, version: data.version };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }
}

export default DatabaseService;
